import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import api, { PRESIGNED_URL_MAX_COUNT } from '../api/client';
import type { NeraAssessment } from '../api/client';
import DateCalendar from './DateCalendar';
import NeraTagFilterRow from './dataviewer/NeraTagFilterRow';
import {
  getAssessmentFilterUnion,
  getAssessmentMetrics,
  assessmentMetricsReady,
  matchesExcludeTags,
  matchesIncludeGroups,
  normalizeMetrics,
  type MetricsMap,
} from './dataviewer/utils';

const CAMERAS = ['right', 'left', 'bottom'] as const;
type CameraKey = (typeof CAMERAS)[number];

type NeraFocusTarget = {
  assessmentId: number;
  date: string;
};

type Props = {
  focus?: NeraFocusTarget | null;
  onFocusConsumed?: () => void;
};

/** Fixed thumbnail slot (images are uniform; avoids layout jump while presigns load). */
const THUMB_SLOT_HEIGHT = 200;
const INITIAL_VISIBLE_ROWS = 12;
const LOAD_MORE_ROWS = 12;
/** Rows per page when infinite scroll is off (matches earlier NERA viewer). */
const PAGE_SIZE = 3;
const TAGS_CHUNK = 300;

function collectS3UrlsForAssessments(rows: NeraAssessment[]): string[] {
  const out: string[] = [];
  for (const a of rows) {
    for (const cam of CAMERAS) {
      const s3 = a[`${cam}_url` as keyof NeraAssessment] as string | null;
      if (s3) out.push(s3);
    }
  }
  return out;
}

async function fetchImageMetadataForUrls(
  urls: string[]
): Promise<{ tags: Record<string, string[]>; metrics: Record<string, MetricsMap> }> {
  const dedup = Array.from(new Set(urls));
  const tags: Record<string, string[]> = {};
  const metrics: Record<string, MetricsMap> = {};
  for (let i = 0; i < dedup.length; i += TAGS_CHUNK) {
    const chunk = dedup.slice(i, i + TAGS_CHUNK);
    const res = await api.dataviewer.getMetadataBatch(chunk);
    const results = res.data.results || {};
    for (const u of chunk) {
      const row = results[u];
      tags[u] = row?.tags ?? [];
      metrics[u] = normalizeMetrics(row?.metrics);
    }
  }
  return { tags, metrics };
}

const NeraViewer: React.FC<Props> = ({ focus = null, onFocusConsumed }) => {
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [currentMonth, setCurrentMonth] = useState<string>(() => {
    const today = new Date();
    return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`;
  });
  const [datesWithData, setDatesWithData] = useState<string[]>([]);

  const [robots, setRobots] = useState<string[]>([]);
  const [robotFilter, setRobotFilter] = useState<string>('');

  const [assessments, setAssessments] = useState<NeraAssessment[]>([]);
  const [presignedUrls, setPresignedUrls] = useState<Map<string, string>>(new Map());
  const [tagsByUrl, setTagsByUrl] = useState<Record<string, string[]>>({});
  const [metricsByUrl, setMetricsByUrl] = useState<Record<string, MetricsMap>>({});
  const [metadataLoading, setMetadataLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [includeGroups, setIncludeGroups] = useState<string[][]>([[]]);
  const [activeIncludeGroupIndex, setActiveIncludeGroupIndex] = useState(0);
  const [includeTagInput, setIncludeTagInput] = useState('');
  const [excludeTags, setExcludeTags] = useState<string[]>([]);
  const [excludeTagInput, setExcludeTagInput] = useState('');

  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);
  const [useInfiniteScroll, setUseInfiniteScroll] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [visibleRowCount, setVisibleRowCount] = useState(INITIAL_VISIBLE_ROWS);
  const [visibleCameras, setVisibleCameras] = useState<Set<CameraKey>>(new Set(CAMERAS));

  const [selectedImageUrls, setSelectedImageUrls] = useState<Set<string>>(new Set());
  const [lastSelectedIndex, setLastSelectedIndex] = useState(-1);
  const [tagInput, setTagInput] = useState('');
  const [tagToRemove, setTagToRemove] = useState('');
  const [addingTags, setAddingTags] = useState(false);
  const [removingTag, setRemovingTag] = useState(false);

  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const tagsFetchedRef = useRef<Set<string>>(new Set());
  const presignedUrlsRef = useRef<Map<string, string>>(presignedUrls);
  const [highlightId, setHighlightId] = useState<number | null>(null);
  const pendingFocusRef = useRef<NeraFocusTarget | null>(null);

  presignedUrlsRef.current = presignedUrls;

  const resetTagFilters = useCallback(() => {
    setIncludeGroups([[]]);
    setActiveIncludeGroupIndex(0);
    setIncludeTagInput('');
    setExcludeTags([]);
    setExcludeTagInput('');
  }, []);

  useEffect(() => {
    api.nera
      .getRobots()
      .then((res) => setRobots(res.data.robots))
      .catch((err) => console.error('Failed to load NERA robots:', err));
  }, []);

  useEffect(() => {
    api.nera
      .getDatesWithData({ month: currentMonth })
      .then((res) => setDatesWithData(res.data.dates))
      .catch((err) => console.error('Failed to load NERA dates:', err));
  }, [currentMonth]);

  const fetchAssessments = useCallback(
    async (
      date: string,
      robot?: string,
      options?: { preserveTagFilters?: boolean; refreshCalendar?: boolean }
    ) => {
      const preserveTagFilters = options?.preserveTagFilters ?? false;
      setLoading(true);
      setError('');
      setAssessments([]);
      setPresignedUrls(new Map());
      setTagsByUrl({});
      setMetricsByUrl({});
      tagsFetchedRef.current = new Set();
      setVisibleRowCount(INITIAL_VISIBLE_ROWS);
      setCurrentPage(1);
      setSelectedImageUrls(new Set());
      setLastSelectedIndex(-1);
      if (!preserveTagFilters) {
        resetTagFilters();
      }

      try {
        const params: { date: string; robot_name?: string } = { date };
        if (robot) params.robot_name = robot;

        const res = await api.nera.getAssessments(params);
        const rows = res.data.assessments;
        setAssessments(rows);

        const allUrls = collectS3UrlsForAssessments(rows);
        if (allUrls.length > 0) {
          setMetadataLoading(true);
          try {
            const { tags: tagResults, metrics: metricsResults } = await fetchImageMetadataForUrls(allUrls);
            for (const u of allUrls) {
              tagsFetchedRef.current.add(u);
            }
            setTagsByUrl(tagResults);
            setMetricsByUrl(metricsResults);
          } catch (metaErr) {
            console.error('Failed to load NERA metadata:', metaErr);
          } finally {
            setMetadataLoading(false);
          }
        }

        if (options?.refreshCalendar) {
          api.nera
            .getDatesWithData({ month: currentMonth })
            .then((r) => setDatesWithData(r.data.dates))
            .catch((err) => console.error('Failed to refresh NERA dates:', err));
        }
      } catch (err: any) {
        setError(`Error loading assessments: ${err.message}`);
      } finally {
        setLoading(false);
      }
    },
    [currentMonth, resetTagFilters]
  );

  const handleDateSelect = (date: string) => {
    if (date === selectedDate) {
      setSelectedDate(null);
      setAssessments([]);
      setPresignedUrls(new Map());
      setTagsByUrl({});
      setMetricsByUrl({});
      tagsFetchedRef.current = new Set();
      setVisibleRowCount(INITIAL_VISIBLE_ROWS);
      setCurrentPage(1);
      setSelectedImageUrls(new Set());
      setLastSelectedIndex(-1);
      resetTagFilters();
      return;
    }
    setSelectedDate(date);
    fetchAssessments(date, robotFilter || undefined);
  };

  const handleMonthChange = (monthStr: string) => {
    setCurrentMonth(monthStr);
    setSelectedDate(null);
    setAssessments([]);
    setPresignedUrls(new Map());
    setTagsByUrl({});
    setMetricsByUrl({});
    tagsFetchedRef.current = new Set();
    resetTagFilters();
  };

  const handleRobotChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setRobotFilter(value);
    if (selectedDate) {
      fetchAssessments(selectedDate, value || undefined);
    }
  };

  const handleRefresh = () => {
    if (!selectedDate) return;
    fetchAssessments(selectedDate, robotFilter || undefined, {
      preserveTagFilters: true,
      refreshCalendar: true,
    });
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0;
    }
  };

  const hasIncludeFilter = includeGroups.some((g) => g.length > 0);

  const filteredAssessments = useMemo(() => {
    if (!hasIncludeFilter && excludeTags.length === 0) return assessments;
    return assessments.filter((a) => {
      const union = getAssessmentFilterUnion(a, tagsByUrl, metricsByUrl);
      const includeOk = matchesIncludeGroups(union, includeGroups);
      const excludeOk = matchesExcludeTags(union, excludeTags);
      return includeOk && excludeOk;
    });
  }, [assessments, tagsByUrl, metricsByUrl, includeGroups, excludeTags, hasIncludeFilter]);

  const suggestedTags = useMemo(() => {
    const set = new Set<string>();
    for (const a of assessments) {
      getAssessmentFilterUnion(a, tagsByUrl, metricsByUrl).forEach((t) => set.add(t));
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [assessments, tagsByUrl, metricsByUrl]);

  const allIncludedTagsLower = useMemo(() => {
    const s = new Set<string>();
    includeGroups.forEach((g) => g.forEach((t) => s.add(t.toLowerCase())));
    return s;
  }, [includeGroups]);

  const includeSuggestions = useMemo(() => {
    const notAlreadyIncluded = suggestedTags.filter((tag) => !allIncludedTagsLower.has(tag.toLowerCase()));
    const q = includeTagInput.trim().toLowerCase();
    if (!q) return notAlreadyIncluded;
    return notAlreadyIncluded.filter((tag) => tag.toLowerCase().includes(q));
  }, [suggestedTags, includeTagInput, allIncludedTagsLower]);

  const excludeSuggestions = useMemo(() => {
    const notAlreadyExcluded = suggestedTags.filter(
      (tag) => !excludeTags.map((t) => t.toLowerCase()).includes(tag.toLowerCase())
    );
    const q = excludeTagInput.trim().toLowerCase();
    if (!q) return notAlreadyExcluded;
    return notAlreadyExcluded.filter((tag) => tag.toLowerCase().includes(q));
  }, [suggestedTags, excludeTagInput, excludeTags]);

  const addIncludeTagToGroup = (groupIndex: number, raw: string) => {
    const t = raw.trim();
    if (!t) return;
    setIncludeGroups((prev) =>
      prev.map((g, i) => {
        if (i !== groupIndex) return g;
        if (g.some((x) => x.toLowerCase() === t.toLowerCase())) return g;
        return [...g, t];
      })
    );
    setIncludeTagInput('');
  };

  const removeIncludeTagFromGroup = (groupIndex: number, tag: string) => {
    setIncludeGroups((prev) => {
      const next = prev.map((g, i) => (i === groupIndex ? g.filter((x) => x !== tag) : g));
      const nonEmpty = next.filter((g) => g.length > 0);
      const result = nonEmpty.length > 0 ? nonEmpty : [[]];
      setActiveIncludeGroupIndex((idx) => Math.min(idx, Math.max(0, result.length - 1)));
      return result;
    });
  };

  const addOrGroup = () => {
    setIncludeGroups((prev) => {
      const next = [...prev, []];
      setActiveIncludeGroupIndex(next.length - 1);
      return next;
    });
    setIncludeTagInput('');
  };

  const addExcludeTag = (raw: string) => {
    const t = raw.trim();
    if (!t) return;
    setExcludeTags((prev) => (prev.some((x) => x.toLowerCase() === t.toLowerCase()) ? prev : [...prev, t]));
    setExcludeTagInput('');
  };

  const removeExcludeTag = (tag: string) => {
    setExcludeTags((prev) => prev.filter((x) => x !== tag));
  };

  const visibleAssessments = useMemo(() => {
    if (useInfiniteScroll) {
      return filteredAssessments.slice(0, Math.min(visibleRowCount, filteredAssessments.length));
    }
    const start = (currentPage - 1) * PAGE_SIZE;
    return filteredAssessments.slice(start, start + PAGE_SIZE);
  }, [filteredAssessments, visibleRowCount, useInfiniteScroll, currentPage]);

  const totalPages = Math.ceil(filteredAssessments.length / PAGE_SIZE) || 1;
  const paginatedRangeStart = useInfiniteScroll
    ? 0
    : Math.min((currentPage - 1) * PAGE_SIZE + 1, filteredAssessments.length);
  const paginatedRangeEnd = useInfiniteScroll
    ? 0
    : Math.min(currentPage * PAGE_SIZE, filteredAssessments.length);

  const filtersActive = hasIncludeFilter || excludeTags.length > 0;

  useEffect(() => {
    if (useInfiniteScroll) return;
    const tp = Math.max(1, Math.ceil(filteredAssessments.length / PAGE_SIZE));
    setCurrentPage((p) => (p > tp ? tp : p));
  }, [filteredAssessments.length, useInfiniteScroll]);

  useEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0;
    }
  }, [selectedDate, assessments]);

  // Lazy presign S3 URLs for currently visible rows (and merge into map).
  useEffect(() => {
    if (!visibleAssessments.length) return;
    const need = collectS3UrlsForAssessments(visibleAssessments).filter(
      (u) => !presignedUrlsRef.current.has(u)
    );
    const dedup = Array.from(new Set(need));
    if (dedup.length === 0) return;

    let cancelled = false;
    (async () => {
      try {
        for (let i = 0; i < dedup.length; i += PRESIGNED_URL_MAX_COUNT) {
          const chunk = dedup.slice(i, i + PRESIGNED_URL_MAX_COUNT);
          const urlRes = await api.dataviewer.getPresignedUrls(chunk);
          if (cancelled) return;
          setPresignedUrls((prev) => {
            const m = new Map(prev);
            for (const item of urlRes.data.urls) {
              if (item.presigned_url && item.s3_url) {
                m.set(item.s3_url, item.presigned_url);
              }
            }
            return m;
          });
        }
      } catch (e) {
        console.error('NERA presign batch failed:', e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [visibleAssessments]);

  // Lazy-load Postgres tags and metrics for URLs we have not fetched yet.
  useEffect(() => {
    if (!visibleAssessments.length) return;
    const urls = collectS3UrlsForAssessments(visibleAssessments);
    const need = urls.filter((u) => !tagsFetchedRef.current.has(u));
    const dedup = Array.from(new Set(need));
    if (dedup.length === 0) return;

    let cancelled = false;
    (async () => {
      try {
        for (let i = 0; i < dedup.length; i += TAGS_CHUNK) {
          const chunk = dedup.slice(i, i + TAGS_CHUNK);
          const res = await api.dataviewer.getMetadataBatch(chunk);
          if (cancelled) return;
          const results = res.data.results || {};
          for (const u of chunk) {
            tagsFetchedRef.current.add(u);
          }
          setTagsByUrl((prev) => {
            const next = { ...prev };
            for (const u of chunk) {
              next[u] = results[u]?.tags ?? [];
            }
            return next;
          });
          setMetricsByUrl((prev) => {
            const next = { ...prev };
            for (const u of chunk) {
              next[u] = normalizeMetrics(results[u]?.metrics);
            }
            return next;
          });
        }
      } catch (e) {
        console.error('NERA metadata batch failed:', e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [visibleAssessments]);

  useEffect(() => {
    if (!useInfiniteScroll) return;
    const el = scrollContainerRef.current;
    if (!el || filteredAssessments.length === 0) return;
    const onScroll = () => {
      const { scrollTop, clientHeight, scrollHeight } = el;
      if (scrollTop + clientHeight >= scrollHeight - 120) {
        setVisibleRowCount((n) => Math.min(n + LOAD_MORE_ROWS, filteredAssessments.length));
      }
    };
    el.addEventListener('scroll', onScroll);
    return () => el.removeEventListener('scroll', onScroll);
  }, [filteredAssessments.length, useInfiniteScroll]);

  useEffect(() => {
    if (useInfiniteScroll) return;
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0;
    }
  }, [currentPage, useInfiniteScroll]);

  const focusHandlersRef = useRef({ fetchAssessments, resetTagFilters, onFocusConsumed });
  focusHandlersRef.current = { fetchAssessments, resetTagFilters, onFocusConsumed };

  useEffect(() => {
    if (!focus) return;
  
    const { fetchAssessments, resetTagFilters, onFocusConsumed } = focusHandlersRef.current;
  
    pendingFocusRef.current = focus;
    setHighlightId(focus.assessmentId);
    setRobotFilter('');
    resetTagFilters();
  
    setCurrentMonth(focus.date.slice(0, 7));
    setSelectedDate(focus.date);
    void fetchAssessments(focus.date, undefined);
  
    onFocusConsumed?.();
  }, [focus]);

  useEffect(() => {
    const pending = pendingFocusRef.current;
    if (!pending || loading) return;
    if (selectedDate !== pending.date) return;
  
    const idx = assessments.findIndex((a) => a.id === pending.assessmentId);
    if (idx < 0) {
      setError(`NERA assessment ${pending.assessmentId} not found on ${pending.date}`);
      pendingFocusRef.current = null;
      return;
    }
  
    // Make sure the row is in the rendered slice
    if (useInfiniteScroll) {
      setVisibleRowCount((n) => Math.max(n, idx + 1));
    } else {
      setCurrentPage(Math.floor(idx / PAGE_SIZE) + 1);
    }
  
    // Wait a tick for DOM paint
    requestAnimationFrame(() => {
      const el = document.getElementById(`nera-assessment-${pending.assessmentId}`);
      el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      pendingFocusRef.current = null;
    });
  }, [assessments, loading, selectedDate, useInfiniteScroll]);

  const toggleCamera = (cam: CameraKey) => {
    setVisibleCameras((prev) => {
      const next = new Set(prev);
      if (next.has(cam)) {
        if (next.size > 1) next.delete(cam);
      } else {
        next.add(cam);
      }
      return next;
    });
  };

  const activeCameras = CAMERAS.filter((c) => visibleCameras.has(c));

/** All camera URLs on an assessment (ignores column visibility). */
const urlsForAssessmentAllCameras = (a: NeraAssessment): string[] => {
  const urls: string[] = [];
  for (const cam of CAMERAS) {
    const s3 = a[`${cam}_url` as keyof NeraAssessment] as string | null;
    if (s3) urls.push(s3);
  }
  return urls;
};

const selectableImagesFlat = useMemo(() => {
  const urls: string[] = [];
  for (const assessment of visibleAssessments) {
    for (const cam of activeCameras) {
      const s3 = assessment[`${cam}_url` as keyof NeraAssessment] as string | null;
      if (s3) urls.push(s3);
    }
  }
  return urls;
}, [visibleAssessments, activeCameras]);

const urlToSelectableIndex = useMemo(() => {
  const m = new Map<string, number>();
  selectableImagesFlat.forEach((url, i) => m.set(url, i));
  return m;
}, [selectableImagesFlat]);

const handleImageSelect = (s3Url: string, index: number, event: React.MouseEvent) => {
  const next = new Set(selectedImageUrls);
  if (event.shiftKey && lastSelectedIndex >= 0) {
    const startIdx = Math.min(lastSelectedIndex, index);
    const endIdx = Math.max(lastSelectedIndex, index);
    for (let i = startIdx; i <= endIdx; i++) {
      const url = selectableImagesFlat[i];
      if (url) next.add(url);
    }
  } else {
    if (next.has(s3Url)) next.delete(s3Url);
    else next.add(s3Url);
    setLastSelectedIndex(index);
  }
  setSelectedImageUrls(next);
};

const selectAllFiltered = () => {
  const next = new Set<string>();
  for (const a of filteredAssessments) {
    for (const u of urlsForAssessmentAllCameras(a)) next.add(u);
  }
  setSelectedImageUrls(next);
  setLastSelectedIndex(-1);
};

const toggleAssessmentSelection = (assessment: NeraAssessment) => {
  const urls = urlsForAssessmentAllCameras(assessment);
  if (urls.length === 0) return;
  setSelectedImageUrls((prev) => {
    const next = new Set(prev);
    const allSelected = urls.every((u) => next.has(u));
    if (allSelected) urls.forEach((u) => next.delete(u));
    else urls.forEach((u) => next.add(u));
    return next;
  });
  setLastSelectedIndex(-1);
};

const isAssessmentFullySelected = (assessment: NeraAssessment): boolean => {
  const urls = urlsForAssessmentAllCameras(assessment);
  return urls.length > 0 && urls.every((u) => selectedImageUrls.has(u));
};

const isAssessmentPartiallySelected = (assessment: NeraAssessment): boolean => {
  const urls = urlsForAssessmentAllCameras(assessment);
  if (urls.length === 0) return false;
  const n = urls.filter((u) => selectedImageUrls.has(u)).length;
  return n > 0 && n < urls.length;
};

  const selectedImagesTagsUnion = useMemo(() => {
    const s = new Set<string>();
    Array.from(selectedImageUrls).forEach((u) => {
      (tagsByUrl[u] || []).forEach((t) => {
        if (t && String(t).trim()) s.add(String(t).trim());
      });
    });
    return Array.from(s).sort((a, b) => a.localeCompare(b));
  }, [selectedImageUrls, tagsByUrl]);

  const handleAddTags = async () => {
    const tags = tagInput.split(/\s*,\s*/).map((x) => x.trim()).filter(Boolean);
    if (!tags.length || selectedImageUrls.size === 0) return;
    setAddingTags(true);
    setError('');
    try {
      const res = await api.dataviewer.addTags({
        image_urls: Array.from(selectedImageUrls),
        tags,
      });
      const data = res.data;
      if (data.errors?.length) {
        setError(`Add tags: ${data.successful_updates} updated; errors: ${data.errors.join('; ')}`);
      } else {
        setTagInput('');
      }
      const refresh = await fetchImageMetadataForUrls(Array.from(selectedImageUrls));
      setTagsByUrl((prev) => ({ ...prev, ...refresh.tags }));
      setMetricsByUrl((prev) => ({ ...prev, ...refresh.metrics }));
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Error adding tags');
    } finally {
      setAddingTags(false);
    }
  };

  const handleRemoveTag = async () => {
    const tag = tagToRemove.trim();
    if (!tag || selectedImageUrls.size === 0) return;
    setRemovingTag(true);
    setError('');
    try {
      const res = await api.dataviewer.removeTag({
        image_urls: Array.from(selectedImageUrls),
        tag,
      });
      const data = res.data;
      if (data.errors?.length) {
        setError(`Remove tag: ${data.successful_updates} updated; errors: ${data.errors.join('; ')}`);
      } else {
        setTagToRemove('');
      }
      const refresh = await fetchImageMetadataForUrls(Array.from(selectedImageUrls));
      setTagsByUrl((prev) => ({ ...prev, ...refresh.tags }));
      setMetricsByUrl((prev) => ({ ...prev, ...refresh.metrics }));
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Error removing tag');
    } finally {
      setRemovingTag(false);
    }
  };

  const idColWidth = 168;
  const metricsColWidth = 148;
  const gridTemplate = `${idColWidth}px ${metricsColWidth}px repeat(${activeCameras.length}, minmax(0, 1fr))`;

  const goToPage = (page: number) => {
    const last = Math.max(1, totalPages);
    if (page >= 1 && page <= last) setCurrentPage(page);
  };

  return (
    <div>
      <h2>NERA Viewer</h2>

      <div style={{ marginBottom: '1rem' }}>
        <label style={{ fontWeight: 'bold', marginRight: '0.5rem' }}>Robot:</label>
        <select
          value={robotFilter}
          onChange={handleRobotChange}
          style={{
            padding: '0.4rem 0.8rem',
            borderRadius: '4px',
            border: '1px solid #dee2e6',
            fontSize: '0.95rem',
          }}
        >
          <option value="">All Robots</option>
          {robots.map((r) => (
            <option key={r} value={r}>
              {r}
            </option>
          ))}
        </select>
      </div>

      <div style={{ marginBottom: '2rem' }}>
        <DateCalendar
          selectedDate={selectedDate}
          datesWithRunStarts={[]}
          datesWithData={datesWithData}
          currentMonth={currentMonth}
          onDateSelect={handleDateSelect}
          onMonthChange={handleMonthChange}
        />
      </div>

      {!selectedDate && (
        <p style={{ color: '#666' }}>Select a date from the calendar to view NERA images.</p>
      )}

      {selectedDate && (
        <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'flex-end' }}>
          <button
            type="button"
            onClick={handleRefresh}
            disabled={loading}
            style={{
              padding: '0.4rem 0.75rem',
              fontSize: '0.9rem',
              border: '1px solid #dee2e6',
              borderRadius: '4px',
              background: '#fff',
              cursor: loading ? 'not-allowed' : 'pointer',
            }}
            title="Reload assessments, tags, and metrics for this date"
          >
            {loading ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
      )}

      {error && <div style={{ color: '#dc3545', marginBottom: '1rem' }}>{error}</div>}

      {loading && <p>Loading...</p>}

      {selectedDate && !loading && assessments.length === 0 && !error && (
        <p style={{ color: '#666' }}>No NERA assessments found for {selectedDate}.</p>
      )}

      {assessments.length > 0 && (
        <>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: '0.75rem',
              marginBottom: '1rem',
            }}
          >
            <p style={{ margin: 0, color: '#333' }}>
              {filtersActive ? (
                <>
                  <strong>{filteredAssessments.length}</strong> of <strong>{assessments.length}</strong>{' '}
                  assessment{assessments.length !== 1 ? 's' : ''}
                </>
              ) : (
                <>
                  <strong>{assessments.length}</strong> assessment{assessments.length !== 1 ? 's' : ''}
                </>
              )}{' '}
              for <strong>{selectedDate}</strong>
              {robotFilter && (
                <>
                  {' '}
                  &middot; Robot: <strong>{robotFilter}</strong>
                </>
              )}
              <span style={{ color: '#666', fontSize: '0.9rem' }}>
                {useInfiniteScroll ? (
                  <>
                    {' '}
                    &middot; Showing {visibleAssessments.length} of {filteredAssessments.length} (scroll for more)
                  </>
                ) : (
                  <>
                    {' '}
                    &middot; Rows {paginatedRangeStart}&ndash;{paginatedRangeEnd} of {filteredAssessments.length}
                    {totalPages > 1 && (
                      <>
                        {' '}
                        &middot; Page {currentPage} of {totalPages}
                      </>
                    )}
                  </>
                )}
              </span>
            </p>

            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
              <label
                style={{
                  fontSize: '0.9rem',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.35rem',
                  color: '#333',
                }}
              >
                <input
                  type="checkbox"
                  checked={useInfiniteScroll}
                  onChange={(e) => {
                    const on = e.target.checked;
                    setUseInfiniteScroll(on);
                    setCurrentPage(1);
                    if (on) {
                      setVisibleRowCount(INITIAL_VISIBLE_ROWS);
                    }
                  }}
                />
                Infinite scroll
              </label>
              <span style={{ fontSize: '0.85rem', color: '#555', fontWeight: 'bold' }}>Cameras:</span>
              {CAMERAS.map((cam) => (
                <button
                  key={cam}
                  type="button"
                  onClick={() => toggleCamera(cam)}
                  style={{
                    padding: '0.3rem 0.7rem',
                    border: visibleCameras.has(cam) ? '2px solid #007bff' : '1px solid #dee2e6',
                    borderRadius: '4px',
                    background: visibleCameras.has(cam) ? '#e7f3ff' : '#fff',
                    color: visibleCameras.has(cam) ? '#007bff' : '#666',
                    fontWeight: visibleCameras.has(cam) ? 'bold' : 'normal',
                    cursor: 'pointer',
                    fontSize: '0.85rem',
                  }}
                >
                  {cam.charAt(0).toUpperCase() + cam.slice(1)}
                </button>
              ))}
            </div>
          </div>

          <NeraTagFilterRow
            suggestedTags={suggestedTags}
            emptySuggestionsLabel="No tags or metrics on this date"
            includeGroups={includeGroups}
            activeIncludeGroupIndex={activeIncludeGroupIndex}
            setActiveIncludeGroupIndex={setActiveIncludeGroupIndex}
            includeTagInput={includeTagInput}
            setIncludeTagInput={setIncludeTagInput}
            addIncludeTagToGroup={addIncludeTagToGroup}
            removeIncludeTagFromGroup={removeIncludeTagFromGroup}
            addOrGroup={addOrGroup}
            includeSuggestions={includeSuggestions}
            excludeTags={excludeTags}
            excludeTagInput={excludeTagInput}
            setExcludeTagInput={setExcludeTagInput}
            addExcludeTag={addExcludeTag}
            removeExcludeTag={removeExcludeTag}
            excludeSuggestions={excludeSuggestions}
          />
          {metadataLoading && (
            <p style={{ margin: '0 0 0.75rem', fontSize: '0.85rem', color: '#888' }}>
              Loading tags and metrics…
            </p>
          )}

          {filtersActive && filteredAssessments.length === 0 && (
            <p style={{ margin: '0 0 0.75rem', color: '#666' }}>
              No assessments match the current tag or metric filters.
            </p>
          )}

          <p style={{ margin: '0 0 0.75rem', fontSize: '0.85rem', color: '#666' }}>
            Click a thumbnail to select it; Shift+click for a range; click the Assessment column to
            select/deselect all cameras in that row; double-click a thumbnail for full size.
          </p>

          {filteredAssessments.length > 0 && (
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: '0.5rem',
                marginBottom: '1rem',
                padding: '0.75rem',
                background: '#f8f9fa',
                border: '1px solid #dee2e6',
                borderRadius: '6px',
              }}
            >
              <span style={{ fontSize: '0.9rem', color: '#333' }}>
                <strong>{selectedImageUrls.size}</strong> image
                {selectedImageUrls.size !== 1 ? 's' : ''} selected
              </span>
              <button
                type="button"
                onClick={selectAllFiltered}
                style={{
                  padding: '0.45rem 0.9rem',
                  background: '#0d6efd',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '0.9rem',
                }}
              >
                Select all ({filteredAssessments.length} assessment
                {filteredAssessments.length !== 1 ? 's' : ''})
              </button>
              <input
                type="text"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                placeholder="Tags to add (comma-separated)"
                disabled={selectedImageUrls.size === 0}
                style={{
                  padding: '0.45rem 0.65rem',
                  border: '1px solid #dee2e6',
                  borderRadius: '4px',
                  minWidth: '200px',
                  fontSize: '0.9rem',
                }}
                onKeyDown={(e) => e.key === 'Enter' && handleAddTags()}
              />
              <button
                type="button"
                onClick={handleAddTags}
                disabled={addingTags || !tagInput.trim() || selectedImageUrls.size === 0}
                style={{
                  padding: '0.45rem 0.9rem',
                  background: selectedImageUrls.size > 0 ? '#007bff' : '#ccc',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '4px',
                  cursor:
                    addingTags || !tagInput.trim() || selectedImageUrls.size === 0
                      ? 'not-allowed'
                      : 'pointer',
                  fontSize: '0.9rem',
                }}
              >
                {addingTags ? 'Adding…' : 'Add tags'}
              </button>
              <select
                value={tagToRemove}
                onChange={(e) => setTagToRemove(e.target.value)}
                disabled={selectedImageUrls.size === 0 || selectedImagesTagsUnion.length === 0}
                style={{
                  padding: '0.45rem',
                  borderRadius: '4px',
                  border: '1px solid #dee2e6',
                  fontSize: '0.9rem',
                }}
              >
                <option value="">Remove tag…</option>
                {selectedImagesTagsUnion.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
              <button
                type="button"
                onClick={handleRemoveTag}
                disabled={removingTag || !tagToRemove || selectedImageUrls.size === 0}
                style={{
                  padding: '0.45rem 0.9rem',
                  background: tagToRemove && selectedImageUrls.size > 0 ? '#6c757d' : '#ccc',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '4px',
                  cursor:
                    removingTag || !tagToRemove || selectedImageUrls.size === 0
                      ? 'not-allowed'
                      : 'pointer',
                  fontSize: '0.9rem',
                }}
              >
                {removingTag ? 'Removing…' : 'Remove'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setSelectedImageUrls(new Set());
                  setLastSelectedIndex(-1);
                }}
                disabled={selectedImageUrls.size === 0}
                style={{
                  padding: '0.45rem 0.75rem',
                  marginLeft: 'auto',
                  background: '#fff',
                  border: '1px solid #dee2e6',
                  borderRadius: '4px',
                  cursor: selectedImageUrls.size === 0 ? 'not-allowed' : 'pointer',
                  fontSize: '0.9rem',
                }}
              >
                Deselect all
              </button>
            </div>
          )}

          {metadataLoading && (
            <p style={{ margin: '0 0 0.75rem', fontSize: '0.85rem', color: '#888' }}>
              Loading tags and metrics…
            </p>
          )}

          <div
            ref={scrollContainerRef}
            style={{
              maxHeight: useInfiniteScroll ? '70vh' : undefined,
              overflowY: useInfiniteScroll ? 'auto' : 'visible',
              overflowX: 'hidden',
              border: '1px solid #e9ecef',
              borderRadius: '8px',
              padding: '0.5rem',
              background: '#fff',
            }}
          >
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: gridTemplate,
                gap: '0.5rem',
                marginBottom: '0.5rem',
                textAlign: 'center',
                fontWeight: 'bold',
                fontSize: '0.9rem',
                color: '#555',
              }}
            >
              <div style={{ textAlign: 'left', paddingLeft: '0.25rem' }}>Assessment</div>
              <div>Metrics</div>
              {activeCameras.map((cam) => (
                <div key={cam}>{cam.charAt(0).toUpperCase() + cam.slice(1)}</div>
              ))}
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {visibleAssessments.map((assessment) => (
                <div
                  key={assessment.id}
                  id={`nera-assessment-${assessment.id}`}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: gridTemplate,
                    gap: '0.5rem',
                    border:
                      highlightId === assessment.id
                        ? '2px solid #0d6efd'
                        : '1px solid #dee2e6',
                    borderRadius: '8px',
                    padding: '0.5rem',
                    background: highlightId === assessment.id ? '#e7f1ff' : '#fafafa',
                    alignItems: 'start',
                  }}
                >
<div
  role="button"
  tabIndex={0}
  title="Click to select/deselect all cameras for this assessment"
  onClick={() => toggleAssessmentSelection(assessment)}
  onKeyDown={(e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggleAssessmentSelection(assessment);
    }
  }}
  style={{
    padding: '0.25rem',
    fontSize: '0.85rem',
    color: '#333',
    cursor: 'pointer',
    borderRadius: '4px',
    outline: isAssessmentFullySelected(assessment)
      ? '2px solid #007bff'
      : isAssessmentPartiallySelected(assessment)
        ? '2px dashed #007bff'
        : '2px solid transparent',
        background: isAssessmentFullySelected(assessment)
      ? '#e7f1ff'
      : 'transparent',
  }}
>
  <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
    <input
      type="checkbox"
      readOnly
      checked={isAssessmentFullySelected(assessment)}
      ref={(el) => {
        if (el) el.indeterminate = isAssessmentPartiallySelected(assessment);
      }}
      style={{ cursor: 'pointer', pointerEvents: 'none' }}
      tabIndex={-1}
      aria-hidden
    />
    <strong>NERA Assessment ID</strong>
  </div>
  <div style={{ fontFamily: 'monospace', marginTop: '0.25rem' }}>
    {assessment.id}
  </div>
  {assessment.fish_id && (
    <div style={{ marginTop: '0.5rem', color: '#666' }}>
      Fish:{' '}
      <span style={{ fontFamily: 'monospace' }}>{assessment.fish_id}</span>
    </div>
  )}
  {assessment.collection_date && (
    <div style={{ marginTop: '0.5rem', color: '#666', fontSize: '0.8rem' }}>
      {assessment.collection_date}
    </div>
  )}
</div>
                  <div
                    style={{
                      padding: '0.25rem',
                      fontSize: '0.8rem',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '0.25rem',
                      alignItems: 'flex-start',
                    }}
                  >
                    {!assessmentMetricsReady(assessment, metricsByUrl) ? (
                      <span style={{ color: '#aaa' }}>…</span>
                    ) : Object.keys(getAssessmentMetrics(assessment, metricsByUrl)).length === 0 ? (
                      <span style={{ color: '#aaa' }}>—</span>
                    ) : (
                      Object.entries(getAssessmentMetrics(assessment, metricsByUrl)).map(([key, value]) => (
                        <span
                          key={key}
                          style={{
                            padding: '0.1rem 0.35rem',
                            background: '#d1ecf1',
                            borderRadius: '3px',
                            color: '#0c5460',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {key}: {String(value)}
                        </span>
                      ))
                    )}
                  </div>
                  {activeCameras.map((camera) => {
                    const s3 = assessment[`${camera}_url` as keyof NeraAssessment] as string | null;
                    const url = s3 ? presignedUrls.get(s3) || null : null;
                    const selected = s3 ? selectedImageUrls.has(s3) : false;
                    return (
                      <div key={camera} style={{ textAlign: 'center', minWidth: 0 }}>
                        <div
                          role="button"
                          tabIndex={0}
                          onClick={(e) => {
                            if (!s3) return;
                            const idx = urlToSelectableIndex.get(s3);
                            if (idx !== undefined) handleImageSelect(s3, idx, e);
                          }}
                          onDoubleClick={(e) => {
                            e.preventDefault();
                            if (url) setLightboxUrl(url);
                          }}
                          onKeyDown={(e) => {
                            if ((e.key === 'Enter' || e.key === ' ') && s3) {
                              e.preventDefault();
                              const idx = urlToSelectableIndex.get(s3);
                              if (idx !== undefined) {
                                handleImageSelect(s3, idx, e as unknown as React.MouseEvent);
                              }
                            }
                          }}
                          style={{
                            width: '100%',
                            height: THUMB_SLOT_HEIGHT,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: '#eee',
                            borderRadius: '6px',
                            border: selected ? '3px solid #007bff' : '1px solid #dee2e6',
                            cursor: s3 ? 'pointer' : 'default',
                            boxSizing: 'border-box',
                          }}
                        >
                          {url ? (
                            <img
                              src={url}
                              alt={`${camera} view`}
                              draggable={false}
                              style={{
                                maxWidth: '100%',
                                maxHeight: '100%',
                                width: 'auto',
                                height: 'auto',
                                objectFit: 'contain',
                                display: 'block',
                                borderRadius: '4px',
                                pointerEvents: 'none',
                              }}
                            />
                          ) : s3 ? (
                            <span style={{ color: '#888', fontSize: '0.85rem' }}>Loading…</span>
                          ) : (
                            <span style={{ color: '#999', fontSize: '0.85rem' }}>No image</span>
                          )}
                        </div>
                        <div
                          style={{
                            marginTop: '0.35rem',
                            minHeight: '1.5rem',
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: '0.2rem',
                            justifyContent: 'center',
                          }}
                        >
                          {!s3 ? null : tagsByUrl[s3] === undefined ? (
                            <span style={{ fontSize: '0.7rem', color: '#aaa' }}>Tags…</span>
                          ) : (tagsByUrl[s3] || []).length === 0 ? (
                            <span style={{ fontSize: '0.7rem', color: '#aaa' }}>No tags</span>
                          ) : (
                            (tagsByUrl[s3] || []).map((t) => (
                              <span
                                key={t}
                                style={{
                                  fontSize: '0.7rem',
                                  padding: '0.1rem 0.35rem',
                                  background: '#e9ecef',
                                  borderRadius: '3px',
                                  color: '#495057',
                                }}
                              >
                                {t}
                              </span>
                            ))
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>

          {!useInfiniteScroll && totalPages > 1 && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '0.75rem',
                marginTop: '1.25rem',
              }}
            >
              <button
                type="button"
                onClick={() => goToPage(currentPage - 1)}
                disabled={currentPage === 1}
                style={{
                  padding: '0.4rem 1rem',
                  border: '1px solid #dee2e6',
                  borderRadius: '4px',
                  background: currentPage === 1 ? '#e9ecef' : '#fff',
                  cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                  fontSize: '0.9rem',
                }}
              >
                Previous
              </button>
              <span style={{ fontSize: '0.9rem', color: '#555' }}>
                Page {currentPage} of {totalPages}
              </span>
              <button
                type="button"
                onClick={() => goToPage(currentPage + 1)}
                disabled={currentPage === totalPages}
                style={{
                  padding: '0.4rem 1rem',
                  border: '1px solid #dee2e6',
                  borderRadius: '4px',
                  background: currentPage === totalPages ? '#e9ecef' : '#fff',
                  cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                  fontSize: '0.9rem',
                }}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}

      {lightboxUrl && (
        <div
          onClick={() => setLightboxUrl(null)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            background: 'rgba(0,0,0,0.85)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            cursor: 'pointer',
          }}
        >
          <img
            src={lightboxUrl}
            alt="Full size"
            style={{
              maxWidth: '90vw',
              maxHeight: '90vh',
              objectFit: 'contain',
              borderRadius: '4px',
            }}
          />
        </div>
      )}
    </div>
  );
};

export default NeraViewer;
